import createClient, { type Middleware } from 'openapi-fetch';
import { API_BASE_URL, apiUrl } from './config';
import { ApiError, type ErrorEnvelope } from './errors';
import { clearSession, getAccessToken, renewSession, touchSession } from '@/auth/session';
import type { paths } from './schema';

/**
 * The typed API client. Request and response shapes come from `schema.d.ts`,
 * generated from the committed `openapi.json` — nothing here is hand-written,
 * so a contract change surfaces as a type error rather than a runtime surprise.
 *
 * Regenerate with `pnpm --filter @babysesame/admin codegen`.
 *
 * The generated paths already carry the `/api/v1` prefix, so the base URL is
 * the origin alone — appending the prefix here would produce `/api/v1/api/v1`.
 */
export const api = createClient<paths>({ baseUrl: API_BASE_URL || '/' });

/**
 * Attaches the admin bearer token and resets the idle clock.
 *
 * The token is read per request rather than captured once: a sign-in, a sign-out
 * or an expiry between two renders must take effect on the very next call, and a
 * closed-over token would keep sending a session the app has already discarded.
 */
const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = getAccessToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
      touchSession();
    }
    return request;
  },
  onResponse({ response }) {
    adoptRenewedToken(response);
    return response;
  },
};

/**
 * Adopts a token the server renewed on this response.
 *
 * ⚠️ Applied to EVERY admin response, including failures — a 403 or a 409 is
 * still an authenticated request, and skipping renewal on those would let a
 * session lapse while an admin works through validation errors.
 *
 * The headers are only readable because the API lists them in its CORS
 * `exposedHeaders`. If renewal ever appears to stop working in the browser but
 * not in curl, that allowlist is the first thing to check.
 */
export function adoptRenewedToken(response: Response): void {
  const token = response.headers.get(ADMIN_TOKEN_HEADER);
  const expiresIn = Number(response.headers.get(ADMIN_TOKEN_EXPIRES_HEADER));

  if (token && Number.isFinite(expiresIn) && expiresIn > 0) {
    renewSession(token, expiresIn);
  }
}

/** Must match `ADMIN_TOKEN_HEADER` in the API's admin guard. */
const ADMIN_TOKEN_HEADER = 'x-admin-token';
const ADMIN_TOKEN_EXPIRES_HEADER = 'x-admin-token-expires-in';

api.use(authMiddleware);

/**
 * Normalises a failed `openapi-fetch` call into an `ApiError`.
 *
 * `401` also drops the session. The server is the authority on whether a token
 * is still good; when it says no, holding on to it only produces a screen full
 * of failures instead of a sign-in form.
 */
export function toApiError(response: Response | undefined, body: unknown): ApiError {
  const status = response?.status ?? 0;
  if (status === 401) clearSession();
  return new ApiError(status, body as Partial<ErrorEnvelope> | undefined);
}

/** Unwraps `{ data, error }` into a value or a thrown `ApiError`. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    throw toApiError(result.response, result.error);
  }
  return result.data as T;
}

/**
 * Raw `fetch` for the responses `openapi-fetch` cannot type usefully: the CSV
 * export, which is a stream rather than JSON, and `GET /health/queues`, whose
 * OpenAPI entry declares a 200 with no schema at all.
 */
export async function rawRequest(path: string, init: RequestInit = {}): Promise<Response> {
  return request(apiUrl(path), init);
}

/**
 * The same, for the handful of routes that live OUTSIDE `/api/v1`.
 *
 * ⚠️ `HealthController` is registered outside the version prefix on purpose (05,
 * CLAUDE.md §4.6) so a Compose healthcheck, Caddy and a Kubernetes probe all hit
 * one stable path. `apiUrl` would turn `/health/queues` into
 * `/api/v1/health/queues`, which 404s — hence a second entry point rather than a
 * flag someone forgets to pass.
 */
export async function rawUnversionedRequest(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  return request(`${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`, init);
}

async function request(url: string, init: RequestInit): Promise<Response> {
  const token = getAccessToken();
  if (token) touchSession();

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(0, undefined);
  }

  adoptRenewedToken(response);

  if (!response.ok) {
    if (response.status === 401) clearSession();
    let envelope: Partial<ErrorEnvelope> | undefined;
    try {
      envelope = (await response.json()) as Partial<ErrorEnvelope>;
    } catch {
      envelope = undefined;
    }
    throw new ApiError(response.status, envelope);
  }

  return response;
}
